/**
 * exportImageLayout — how a PDF page image is SIZED and CARRIED in each export
 * format.
 *
 * Excel was the only format that embedded page images; Word and HTML dropped
 * `ExportDrug.pdf` entirely and exported page text, losing the document's real
 * table structure and layout. Now all three embed the pages, which means the
 * sizing rules have to live in ONE place or the three formats drift apart.
 *
 * What belongs here: pure geometry and encoding (how big may a page be in this
 * container, and in what shape does the container want the bytes).
 * What does NOT belong here: which pages to embed, the quality tiers and the
 * byte budget — those stay in `pdfPageImages.ts`, shared by all three formats.
 *
 * No DOM, no fetch, no libraries — so it is trivially unit-testable and runs
 * unchanged in Node when the export moves server-side (see
 * docs/export/SERVER_SIDE_EXPORT_PLAN.md).
 */

/** The three formats that can carry page images. (`.xls` deliberately cannot —
 *  Excel's HTML import supports neither data: URIs nor embedded remote images.) */
export type ExportImageFormat = 'xlsx' | 'docx' | 'html';

export interface Box {
  width: number;
  height: number;
}

/** A page's source dimensions, as `ExportDrugPdf.pages[]` / `EmbeddedPage` carry
 *  them — either may be missing, which is why DEFAULT_PAGE_ASPECT exists. */
export interface PageDims {
  width?: number | null;
  height?: number | null;
}

/** A4 portrait height/width — assumed when a page carries no dimensions. */
export const DEFAULT_PAGE_ASPECT = 1.414;

// Effectively-unbounded height: a worksheet and an HTML column scroll, so only
// their width constrains a page image. Word is the one format with a real page.
const UNBOUNDED = 100_000;

/**
 * The largest box each format will give one page image, in px.
 *
 * - `xlsx`: the drug's column band is COLUMN_WIDTH (80 chars) ≈ 560 px wide.
 * - `docx`: A4 portrait with 1" margins = 6.27" × 9.42" at 96 dpi ≈ 601 × 905,
 *   minus room for the "Page N" caption above the image.
 * - `html`: a CSS max-width; the `<img>` also carries `max-width:100%` so it
 *   scales down inside a narrow column.
 */
export const FORMAT_BOX: Readonly<Record<ExportImageFormat, Box>> = {
  xlsx: { width: 560, height: UNBOUNDED },
  docx: { width: 601, height: 905 },
  html: { width: 900, height: UNBOUNDED },
};

/**
 * Fit a page into a format's box: fill the box width at the page's own aspect
 * ratio, then shrink if that would overflow the box height.
 *
 * Filling the width (rather than never upscaling) is deliberate and matches
 * what the Excel export already did — a regulatory page rendered smaller than
 * its column is harder to read, and the source images are downsampled
 * thumbnails, not originals.
 */
export function fitBox(page: PageDims, box: Box): Box {
  const aspect = page.width && page.height ? page.height / page.width : DEFAULT_PAGE_ASPECT;
  const width = box.width;
  const height = Math.round(width * aspect);
  if (height <= box.height) return { width, height };
  // Too tall for the page (landscape boxes, unusually tall scans): clamp on
  // height instead, so a Word page image never overflows and reflows.
  return { width: Math.round(box.height / aspect), height: box.height };
}

/** `EmbeddedPage.extension` uses exceljs's spelling; the `docx` library accepts
 *  only 'jpg'. Passing 'jpeg' there produces an invalid image part. */
export function docxImageType(extension: 'jpeg' | 'png'): 'jpg' | 'png' {
  return extension === 'png' ? 'png' : 'jpg';
}

const MIME: Readonly<Record<'jpeg' | 'png', string>> = {
  jpeg: 'image/jpeg',
  png: 'image/png',
};

/** Base64 for an ArrayBuffer, in chunks — `String.fromCharCode(...bytes)` on a
 *  whole page image (hundreds of KB) overflows the argument stack. */
function toBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/** A page image as a `data:` URI, for the HTML export — which embeds its images
 *  so the downloaded file still renders with no network (the offline case the
 *  HTML export exists for). Costs 4/3 of the raw bytes; see FORMAT_BYTE_FACTOR. */
export function toDataUri(page: { buffer: ArrayBuffer; extension: 'jpeg' | 'png' }): string {
  return `data:${MIME[page.extension]};base64,${toBase64(page.buffer)}`;
}
